import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useInferenceCreateContext } from "domains/inference/contexts/InferenceProvider";
import {
  getDimensionValues,
  getIsCustomDimensions,
} from "domains/inference/utils/dimensionValues";
import { getIsModelFluxPro1_1Ultra, getIsModelGptImage } from "domains/models/utils";
import Select from "domains/ui/components/Select";
import WithLabelAndTooltip from "domains/ui/components/WithLabelAndTooltip";

import {
  Box,
  HStack,
  NumberInput,
  NumberInputField,
  Text,
} from "@chakra-ui/react";

export default function SidebarSelectDimensions() {
  const {
    model,
    width,
    minWidth,
    maxWidth,
    setWidth,
    height,
    minHeight,
    maxHeight,
    setHeight,
  } = useInferenceCreateContext();

  const [tmpWidth, setTmpWidth] = useState(width);
  const [tmpHeight, setTmpHeight] = useState(height);

  useEffect(() => {
    setTmpWidth(width);
    setTmpHeight(height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);

  const submitDimensions = useCallback(() => {
    const width = setWidth(tmpWidth);
    const height = setHeight(tmpHeight);
    setTmpWidth(width);
    setTmpHeight(height);
  }, [setWidth, tmpWidth, setHeight, tmpHeight]);

  const allDimensionValues = useMemo(() => getDimensionValues(model), [model]);

  const onSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const [width, height] = e.target.value.split("x");
      setTmpWidth(parseInt(width));
      setTmpHeight(parseInt(height));
      setWidth(parseInt(width));
      setHeight(parseInt(height));
    },
    [setTmpWidth, setTmpHeight, setWidth, setHeight]
  );

  const isCustomDimensions = useMemo(() => {
    return getIsCustomDimensions({
      model,
      width,
      height,
    });
  }, [model, width, height]);

  const canHaveCustomDimensions = useMemo(() => {
    return !getIsModelFluxPro1_1Ultra(model) && !getIsModelGptImage(model);
  }, [model]);

  return (
    <WithLabelAndTooltip
      label={`Dimensions`}
      labelProps={{
        w: "fit-content",
      }}
    >
      <HStack justify="end" w="100%">
        <NumberInput
          sx={
            !canHaveCustomDimensions
              ? {
                  input: {
                    bg: "transparent !important",
                  },
                }
              : {}
          }
          w="50px"
          borderRadius="sm"
          pointerEvents={!canHaveCustomDimensions ? "none" : "auto"}
          data-testid="inference-builder-width"
          max={maxWidth}
          min={minWidth}
          onBlur={submitDimensions}
          onChange={(newValue) => {
            setTmpWidth(parseInt(newValue));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              submitDimensions();
            }
          }}
          size="xs"
          step={8}
          value={tmpWidth.toString()}
          variant="ghost"
        >
          <NumberInputField />
        </NumberInput>

        <Text color="textSecondary" size="body.md">
          x
        </Text>

        <NumberInput
          sx={
            !canHaveCustomDimensions
              ? {
                  input: {
                    bg: "transparent !important",
                  },
                }
              : {}
          }
          w="50px"
          borderRadius="sm"
          pointerEvents={!canHaveCustomDimensions ? "none" : "auto"}
          data-testid="inference-builder-height"
          max={maxHeight}
          min={minHeight}
          onBlur={submitDimensions}
          onChange={(newValue) => {
            setTmpHeight(parseInt(newValue));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              submitDimensions();
            }
          }}
          size="xs"
          step={8}
          value={tmpHeight.toString()}
          variant="ghost"
        >
          <NumberInputField />
        </NumberInput>
        <Box
          sx={{
            ".chakra-select": {
              position: "absolute",
            },
            ".chakra-select:hover": {
              bgColor: "transparent !important",
              "& + .chakra-select__icon-wrapper": {
                backgroundColor: "whiteAlpha.50 !important",
              },
            },
            ".chakra-select__wrapper": {
              display: "flex",
              alignItems: "center",
            },
            ".chakra-select__icon-wrapper": {
              position: "unset",
              transform: "unset",
            },
            ".dimension-option": {
              fontSize: "var(--input-font-size)",
            },
          }}
        >
          <Select
            onChange={onSelectChange}
            value={isCustomDimensions ? "custom" : `${width}x${height}`}
          >
            {allDimensionValues.map((dimension, index) => (
              <option
                key={index}
                value={dimension.width + "x" + dimension.height}
                label={`${dimension.width}x${dimension.height} (${dimension.ratio})`}
                className="dimension-option"
              />
            ))}
            {isCustomDimensions && (
              <option
                value="custom"
                label="Custom"
                className="dimension-option"
              />
            )}
          </Select>
        </Box>
      </HStack>
    </WithLabelAndTooltip>
  );
}
